import {
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { bool, func, object } from "prop-types";
import { useSnackbar } from "notistack";
import { useAddRole, useUpdateRole } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { KeyTextField } from "@/components";

interface AddRoleRow {
  key?: string;
  role?: string;
}

interface AddRolePageProps {
  open?: boolean;
  handleClose: (open: boolean) => void;
  row?: AddRoleRow;
}

export const AddRolePage = (props: AddRolePageProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    open: false,
  };

  const [inputRole, setInputRole] = useState<string>("");
  const [inputKey, setInputKey] = useState<string>("");

  // Use props.open directly instead of maintaining state
  const open = props.open ?? propsDefault.open;

  useEffect(() => {
    if (props.row && props.row.key) {
      // Use setTimeout to defer state updates and avoid cascading renders
      setTimeout(() => {
        setInputRole(props.row?.role || "");
        setInputKey(props.row?.key || "");
      }, 0);
    } else {
      setTimeout(() => {
        setInputRole("");
        setInputKey("");
      }, 0);
    }
  }, [props.row]);

  const handleClose = () => {
    props.handleClose(false);
  };

  const resetForm = useCallback(() => {
    setInputKey("");
    setInputRole("");
  }, []);

  const { addRole, loading: updatingAdd } = useAddRole(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The new role has been added."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateRole, loading: updatingEdit } = useUpdateRole(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The role has been updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables = {
      key: inputKey,
      role: inputRole,
    };
    if (props.row.key) {
      updateRole({
        variables: variables,
        refetchQueries: ["getAllRoles"],
      }).then();
    } else {
      addRole({
        variables: variables,
        refetchQueries: ["getAllRoles"],
      }).then();
    }
  };

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputRole(event.target.value);
  };

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputKey(event.target.value);
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 450,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.key && gettext("Add a new role")}
              {props.row.key && gettext("Edit Role")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="text"
                    label={gettext("Role")}
                    placeholder={gettext("Role")}
                    size="small"
                    required
                    fullWidth
                    value={inputRole}
                    onChange={handleRoleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <KeyTextField
                    type="text"
                    label={gettext("Key")}
                    placeholder={gettext(
                      "Role key, Alpha numeric and dash only",
                    )}
                    size="small"
                    required
                    fullWidth
                    value={inputKey}
                    onChange={handleKeyChange}
                    disabled={!!props.row.key}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid container spacing={4} sx={{ size: "100%" }}>
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={updatingAdd || updatingEdit}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

AddRolePage.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
